import type { ReactNode } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { TerminalMessage } from "@aerelontech/react-terminal";

// Assistant replies are markdown, and were being shown as source (TODO §AI terminal).
//
// Every chat model writes `**bold**`, numbered lists and fenced code whether or not it was
// asked to — that is what instruction-tuned models emit — so a console that prints the raw
// characters is not neutral, it is broken-looking. The terminal package renders message text
// verbatim by design (it is a generic control and cannot assume markdown), and it exposes
// `slots.renderMessage` for exactly this. So the parsing lives here, in the app, and
// `packages/react-terminal` stays unforked.
//
// Only ASSISTANT messages are parsed. A user's own line is shown exactly as they typed it —
// re-rendering someone's input as markdown would eat their asterisks and underscores — and
// system/error lines are terse status text, not prose.
//
// Raw HTML is not enabled (no `rehype-raw`), so model output cannot inject markup into the
// studio; links are opened out of the app rather than navigating it away.

const REMARK_PLUGINS = [remarkGfm];

// The package's own class names (`DEFAULT_CLASSNAMES`), mirrored so a custom-rendered message
// is indistinguishable from a default one — same padding, same role colours, same caret.
function messageClass(m: TerminalMessage): string {
  return [
    "rt-message",
    m.role === "user" && "rt-message--user",
    m.role === "assistant" && "rt-message--assistant",
    m.role === "system" && "rt-message--system",
    m.error && "rt-message--error",
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * `slots.renderMessage` for `<ReactTerminal>`. Assistant text becomes markdown; everything
 * else keeps the default verbatim rendering.
 */
export function renderTerminalMessage(m: TerminalMessage): ReactNode {
  const markdown = m.role === "assistant" && !m.error && m.content.length > 0;
  return (
    <div className={messageClass(m)} data-role={m.role}>
      {markdown ? (
        // `.ai-terminal-markdown` drops the list's `white-space: pre-wrap` — with real block
        // elements the source newlines would otherwise be rendered twice — and styles the
        // elements against the terminal's own --rt-* tokens (see src/styles.css).
        <div className="rt-message-content ai-terminal-markdown">
          <Markdown
            remarkPlugins={REMARK_PLUGINS}
            components={{
              // Open links outside the app: in the browser this keeps the studio from being
              // navigated away with a deck open, and in the desktop shell `will-navigate`
              // hands anything off-origin to the OS browser anyway.
              a: ({ children, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
            }}
          >
            {m.content}
          </Markdown>
        </div>
      ) : (
        <span className="rt-message-content">{m.content}</span>
      )}
      {/* Streaming caret, exactly as the package draws it. */}
      {m.streaming && <span className="rt-caret" aria-hidden="true" />}
    </div>
  );
}
